import { CuentasContablesInsertTicketCommandProps } from "api/Commands/CuentasContablesInsertTicketCommand"
import { insertTickerAdapter } from "api/Ticket.adapter"
import { insertTicket, updateTicket } from "api/Ticket.service"
import { TIPO_FICHERO } from "api/Types/TipoFichero"
import HeaderWrapper from "common/components/HeaderWrapper"
import notification from "common/utils/notification"
import { ErrorMessage, Form, withFormik } from "formik"
import { TFunction } from "i18next"
import React from "react"
import { useTranslation } from "react-i18next"
import { UUIDType, v4 as uuidv4 } from "uuid"
import * as Yup from "yup"
import ContentFromTickets from "../ContentFormTickets"
import DropZoneTickets from "../DropZoneTickets"
import updateTicketAdapter from "./updateTicket.adapter"

export interface ErrorsTicketsFormProps {
  numero?: string
  nombre?: string
  fechaEmision?: string
  cuentasContables?: {
    bancariaId?: string
    categoriaId?: string
  }
  empleadoId?: string
  empresaId?: string
  fichero?: string | null
  categoria?: {
    cuentaContableId?: string
    icono?: string
    nombre?: string
  }
  conceptos?: {
    cantidad?: string
    nombre?: string
    precio?: string
  }[]
}
export interface TouchedTicketsFormProps {
  empresaId?: boolean
  nombre?: boolean
  numero?: boolean
  fechaEmision?: boolean
  cuentasContables?: {
    bancariaId?: boolean
    categoriaId?: boolean
  }
  empleadoId?: string
  fichero?: string | null
  categoria?: {
    cuentaContableId?: string
    icono?: string
    nombre?: string
  }
  conceptos?: {
    id?: boolean
    nombre?: boolean
    cantidad?: boolean
    precio?: boolean
    total?: boolean
  }[]
}
export interface ConceptosTicketsProps {
  id?: UUIDType | string | null
  nombre: string
  cantidad: number
  precio: number
  total: number
}
export interface InsertTicketProps {
  conceptos?: ConceptosTicketsProps[]
  cuentasContables?: CuentasContablesInsertTicketCommandProps
  empleadoId?: string | null
  empresaId?: string | null
  fechaEmision?: string | null
  fichero?: string | null
  ficheroType?: number | null
  categoria?: {
    cuentaContableId?: string
    icono?: string
    nombre?: string
  }
  nombre?: string | null
  numero?: string | null
}

interface FormTicketProps {
  values: InsertTicketProps
  handleSubmit: () => Promise<void>
  touched: TouchedTicketsFormProps
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldValue: (arg: string, value: $TSMigrationFixMe) => void
  errors: ErrorsTicketsFormProps
  validateField: (field: string) => void
  isNew: boolean
  goBack: () => void
  isMobile: boolean
  setIsMobile: React.Dispatch<React.SetStateAction<boolean>>
  imageData: string
  setImageData: React.Dispatch<React.SetStateAction<string>>
  isSubmitting: boolean
}

const FormTicket = ({
  values,
  handleSubmit,
  touched,
  setFieldValue,
  errors,
  validateField,
  isNew,
  goBack,
  isMobile,
  setIsMobile,
  imageData,
  setImageData,
  isSubmitting,
}: FormTicketProps) => {
  const { t } = useTranslation()

  return (
    <Form className='tw-w-full'>
      <HeaderWrapper
        icon='arrow-left'
        onClickIcon={() => goBack()}
        classNameIcon='tw-w-10 tw-h-10 tw-flex tw-justify-center tw-items-center tw-shadow tw-rounded  tw-cursor-pointer'
        title={
          isNew ? t("tickets.form.newTicket") : t("tickets.form.editTitle")
        }
      >
        <div className='tw-hidden lg:tw-flex'>
          <div
            onClick={handleSubmit}
            className='tw-cursor-pointer tw-rounded tw-flex tw-justify-center tw-items-center tw-bg-secondary hover:tw-bg-secondary-over tw-text-simple-10 tw-w-32 tw-h-10'
          >
            {isNew
              ? t("tickets.form.createTitle")
              : t("tickets.form.editTitle")}
          </div>
        </div>
      </HeaderWrapper>
      <div className='lg:tw-w-full lg:tw-h-full lg:tw-flex lg:tw-flex-row lg:tw-justify-start lg:tw-p-6 sm:tw-mx-auto sm:tw-max-w-lg lg:tw-max-w-none'>
        <div className='tw-w-full'>
          <div className='lg:tw-grid lg:tw-grid-cols-3 lg:tw-gap-4'>
            <div className='lg:tw-col-span-2'>
              <ContentFromTickets
                setFieldValue={setFieldValue}
                values={values}
                errors={errors}
                touched={touched}
                isNew={isNew}
                isMobile={isMobile}
                setIsMobile={setIsMobile}
                handleSubmit={handleSubmit}
                imageData={imageData}
                setImageData={setImageData}
                isSubmitting={isSubmitting}
                validateField={validateField}
              />
            </div>
            <div className='tw-h-full tw-hidden lg:tw-block'>
              <div className='tw-h-full tw-max-h-full tw-overflow-auto'>
                <DropZoneTickets
                  setFieldValue={setFieldValue}
                  values={values}
                  touched={touched}
                  errors={errors}
                  isMobile={isMobile}
                  imageData={imageData}
                  setImageData={setImageData}
                />
              </div>
              <ErrorMessage
                name='fichero'
                component='div'
                className='!tw-block !tw-w-full !tw-text-left !tw-text-danger'
              />
            </div>
          </div>
        </div>
      </div>
    </Form>
  )
}

const FormTicketCreate = withFormik({
  mapPropsToValues: ({
    data,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: $TSMigrationFixMe
    isNew: boolean
    t: TFunction
    goBack: () => void
    isMobile: boolean
    setIsMobile: React.Dispatch<React.SetStateAction<boolean>>
    imageData: string
    setImageData: React.Dispatch<React.SetStateAction<string>>
  }) => {
    return {
      categoria: {
        cuentaContableId:
          data?.categoria?.cuentaContableId ??
          data?.cuentasContables?.categoriaId,
        icono: data?.categoria?.icono,
        nombre: data?.categoria?.nombre,
      },
      empresaId: data?.empresa?.id,
      nombre: data?.nombre,
      numero: data?.numero,
      fechaEmision: data?.fechaEmision,
      contableEmpresaId: null,
      cuentasContables: {
        bancariaId: data?.cuentasContables?.bancariaId,
        categoriaId: data?.cuentasContables?.categoriaId,
      },
      empleadoId: data?.empleado?.id,
      conceptos: data?.conceptos ?? [
        { id: uuidv4(), nombre: null, cantidad: 0, precio: null, total: 0 },
      ],
      fichero: data?.fichero,
      ficheroType: data?.fichero ? TIPO_FICHERO.PDF : null,
    }
  },
  validationSchema: ({ t }: { t: TFunction }) =>
    Yup.object().shape(
      {
        categoria: Yup.object().shape({
          cuentaContableId: Yup.string()
            .required(`${t("tickets.form.errors.categoryWithoutCC")}`)
            .nullable(),
          icono: Yup.string().nullable(),
          nombre: Yup.string()
            .required(`${t("tickets.form.errors.category")}`)
            .nullable(),
        }),
        empresaId: Yup.string().nullable(),
        numero: Yup.string().nullable(),
        nombre: Yup.string()
          .required(`${t("tickets.form.errors.nombre")}`)
          .nullable(),
        fechaEmision: Yup.date()
          .required(`${t("tickets.form.errors.fecha")}`)
          .nullable(),
        contableEmpresaId: Yup.string().nullable(),
        cuentasContables: Yup.object()
          .shape({
            bancariaId: Yup.string()
              .nullable()
              .test(
                "bancariaId-not-error",
                `${t("tickets.form.errors.bancariaIdReembolso")}`,
                (value) => value !== "error"
              ),
            categoriaId: Yup.string().nullable(),
          })
          .test(
            "bancariaId-required-when-no-empleado",
            function bancariaIdRequiredWhenNoEmpleado(value) {
              const empleadoId = this.parent?.empleadoId
              const hasBancariaId =
                value?.bancariaId !== undefined &&
                value?.bancariaId !== null &&
                `${value?.bancariaId}`.trim() !== ""
              const isRequired = Boolean(empleadoId || hasBancariaId)

              if (empleadoId || hasBancariaId) return isRequired

              return this.createError({
                path: "cuentasContables.bancariaId",
                message: String(t("tickets.form.errors.bancariaId")),
              })
            }
          ),
        empleadoId: Yup.string().nullable(),
        fichero: Yup.mixed()
          .required(`${t("tickets.form.errors.fichero")}`)
          .nullable(),
        conceptos: Yup.array().of(
          Yup.object().shape({
            id: Yup.string().default(uuidv4()),
            nombre: Yup.string().required(
              `${t("tickets.form.errors.conceptos.nombre")}`
            ),
            cantidad: Yup.number().required(
              `${t("tickets.form.errors.conceptos.cantidad")}`
            ),
            precio: Yup.number().required(
              `${t("tickets.form.errors.conceptos.precio")}`
            ),
          })
        ),
      },
      [["empleadoId", "cuentasContables"]]
    ),
  handleSubmit: async (values, { props: { goBack, t, data, isNew } }) => {
    const neededData = Object.assign(values, {
      ...values,
      id: data?.id,
      cuentasContables: {
        ...values.cuentasContables,
        bancariaId: values?.empleadoId
          ? null
          : isNew
          ? values.contableEmpresaId
          : values.cuentasContables.bancariaId,
        categoriaId: isNew
          ? values.categoria.cuentaContableId
          : values.cuentasContables.categoriaId,
      },
      empleadoId: values?.empleadoId,
      fichero: {
        ...(values.fichero === data?.fichero
          ? { id: data?.ficheroId }
          : {
              ficheroAsBase64:
                values.fichero?.ficheroAsBase64 ?? values.fichero,
              tipo: values.ficheroType,
            }),
      },
    })

    const resp = isNew
      ? await insertTicket(insertTickerAdapter(neededData))
      : await updateTicket(updateTicketAdapter(neededData))

    if (resp.status) {
      goBack()
      notification.success({
        body: `${t("notification.ticketCorrect")}`,
      })
    } else {
      notification.success({
        body: `${t("notification.errorOcurred")}`,
      })
    }
  },
})(FormTicket)

export default FormTicketCreate
